/**
 * HubSettingsGear — the one settings gear in a page head (Settings and the gear,
 * Part 1 #5, canvas v25 board "3 · The gear"; Michael 2026-10-29: "these look
 * solid - to staging they go"). The 2.1 page is the program session's to write.
 *
 * - The look is `HubIconButton`'s, unchanged: 40 square, radius 4, a 1px `--rule`
 *   hairline on `--paper`, the rail's own `settings` glyph in `--ink-soft`; hover
 *   `--paper-2` with the glyph in `--ink`; focus a 3px `--indigo` outline. On a
 *   hero or band (`onCarbon`) it is 32, on `--carbon-3` in `--on-carbon`.
 * - It is a plain LINK, not a button, so ⌘-click opens a new tab:
 *   `/settings/<section>?from=<this page>#<panel>`. `from` draws only the way back.
 * - It only ever goes to a Settings section, never opens a menu. A view choice that
 *   used to hide behind a gear belongs in the page's ⋯ menu.
 * - Who sees it (question 1 A): each target is checked against the same gate the
 *   settings menu uses (`visibleSettingsSections`); the first one this person may
 *   open wins (question 2 A — a personal fallback goes last). With none, NO gear:
 *   gated means absent, never greyed out.
 * - The tooltip (400ms, `HubTooltip`) and accessible name: "<Surface> settings", or
 *   "Your <surface> settings" for a personal target.
 */
import React from 'react';
import { Box } from '@mui/material';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { PIcon } from '../v2/icons';
import { HubTooltip } from './HubTooltip';
import { useAuth } from '../../contexts/AuthContext';
import { allowedSettingsSectionIds, type SettingsSectionId } from '../settings/sectionRegistry';

export interface HubSettingsTarget {
  /** The settings section (`/settings/<section>`). */
  section: SettingsSectionId;
  /** A panel's anchor inside it (`#stage-playbook`); it is ringed on arrival. */
  panel?: string;
  /** The person's own settings (the fallback when the admin section is closed to them). */
  personal?: boolean;
}

export type HubSettingsProp = HubSettingsTarget | HubSettingsTarget[];

export interface HubSettingsGearProps {
  settings: HubSettingsProp;
  /** The surface's name for the tooltip ("Projects"). */
  surface: string;
  /** On a hero or a page band: 32, on carbon. */
  onCarbon?: boolean;
  testId?: string;
}

/** The href the gear goes to, for a target (exported for the surfaces that build their own head). */
export function settingsGearHref(target: HubSettingsTarget, from: string): string {
  const q = from ? `?from=${encodeURIComponent(from)}` : '';
  return `/settings/${target.section}${q}${target.panel ? `#${target.panel}` : ''}`;
}

/** The first target this person may open, or null (then no gear at all). */
export function useSettingsTarget(settings: HubSettingsProp | undefined): HubSettingsTarget | null {
  const auth = useAuth();
  if (!settings) return null;
  // Tolerant of a partial auth (a test's mock, a page outside the provider): no answer = no gear.
  const allowed = allowedSettingsSectionIds({
    hasAdminSettings: typeof auth?.hasTab === 'function' ? auth.hasTab('settings') : false,
    hasAction: (a) => (typeof auth?.hasAction === 'function' ? auth.hasAction(a) : false),
  });
  const list = Array.isArray(settings) ? settings : [settings];
  return list.find((t) => allowed.includes(t.section)) ?? null;
}

const SX = {
  transition: 'background var(--dur-state, 120ms) var(--ease-out), color var(--dur-state, 120ms) var(--ease-out), transform var(--dur-state, 120ms) var(--ease-out)',
  '&:hover': { background: 'var(--paper-2)', color: 'var(--ink)' },
  '&:active': { transform: 'scale(.975)' },
  '&:focus-visible': { outline: '3px solid var(--indigo)', outlineOffset: '2px' },
} as const;
const SX_CARBON = {
  ...SX,
  '&:hover': { background: 'var(--carbon-2)', color: 'var(--on-carbon)' },
} as const;

export function HubSettingsGear(props: HubSettingsGearProps): React.ReactElement | null {
  // The target first: a page with no gear never touches the router. Outside a router (a
  // part rendered on its own in a test) there is nowhere to go, so there is no gear.
  const target = useSettingsTarget(props.settings);
  const inRouter = useInRouterContext();
  if (!target || !inRouter) return null;
  return <GearLink {...props} target={target} />;
}

function GearLink({ surface, onCarbon, testId, target }: HubSettingsGearProps & { target: HubSettingsTarget }): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const href = settingsGearHref(target, `${location.pathname}${location.search}`);
  const label = target.personal ? `Your ${surface.toLowerCase()} settings` : `${surface} settings`;
  const size = onCarbon ? 32 : 40;
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    // A plain link: ⌘ / Ctrl / Shift / middle click keep the browser's own behaviour.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(href);
  };
  return (
    <HubTooltip title={label}>
      <Box
        component="a"
        href={href}
        onClick={onClick}
        aria-label={label}
        data-testid={testId ?? 'hub-settings-gear'}
        sx={onCarbon ? SX_CARBON : SX}
        style={{
          width: size, height: size, flex: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 4, textDecoration: 'none',
          border: onCarbon ? '1px solid var(--rule-on-carbon)' : '1px solid var(--rule)',
          background: onCarbon ? 'var(--carbon-3)' : 'var(--paper)',
          color: onCarbon ? 'var(--on-carbon)' : 'var(--ink-soft)',
        }}
      >
        <PIcon name="settings" size={onCarbon ? 14 : 16} color="currentColor" />
      </Box>
    </HubTooltip>
  );
}

export default HubSettingsGear;
